import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Container,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { GridSearchIcon } from '@mui/x-data-grid';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CalendarSyncPair, SlotRebuildStatus, getCalendarSyncPairsAPI } from 'services/selfSchedulingServices';
import COLORS from 'theme/colors';
import { groupByLocation } from '../SyncCalendar/syncCalendarGroups';
import { AutoRefreshCountdown, useAutoRefresh } from '../SyncCalendar/AutoRefresh';
import { StatusChip, formatDate } from '../SyncCalendar/syncStatusDisplay';
import BreakEventsTable, { sortedBreaks } from './BreakEventsTable';
import { currentRun } from './currentRun';
import MessageCell from './MessageCell';
import { TRIGGER_LABELS } from './selfSchedulingJobs';

/** MM-1169: columns of the latest-run table. */
const RUN_COLUMNS = [
  'Location / Provider',
  'Trigger type',
  'Queued',
  'Started',
  'Completed',
  // MM-1198: status and retries in one column
  'Status',
  'Next retry',
  'Cause of error',
];

/** MM-1198: whether a location + provider has stored breaks - only then its row expands. */
const hasBreaks = (pair: CalendarSyncPair) => sortedBreaks(pair).length > 0;


/** MM-1198: a location + provider's key in the expanded-rows set. */
const pairKey = (pair: CalendarSyncPair) => `${pair.officeId}-${pair.providerId}`;

/** MM-1169: status filter chips with their counts - All selected by default; empty status = all. */
const STATUS_FILTERS: {
  status: SlotRebuildStatus | '';
  label: string;
  color: 'default' | 'success' | 'warning' | 'error' | 'info';
}[] = [
  { status: '', label: 'All', color: 'default' },
  { status: 'PENDING', label: 'Pending', color: 'default' },
  { status: 'IN_PROGRESS', label: 'In progress', color: 'info' },
  { status: 'COMPLETED', label: 'Completed', color: 'success' },
  { status: 'COMPLETED_WITH_ERRORS', label: 'Completed with errors', color: 'warning' },
  { status: 'FAILED', label: 'Failed (retrying)', color: 'error' },
  { status: 'RETRY_LIMIT_REACHED', label: 'Retry limit reached', color: 'error' },
];

/**
 * MM-1169: a pair's status, empty (never rebuilt) counted as Pending. MM-1198: the status of its current run (see
 * currentRun) - a waiting or running break counts, not only the full rebuild.
 */
const statusOf = (pair: CalendarSyncPair): SlotRebuildStatus => currentRun(pair).status ?? 'PENDING';

/**
 * MM-1169: System Monitoring -> Sync Jobs. One place for the automated sync jobs: what each is for, when it runs
 * and its automatic retry, and the latest run of each. For now one section, Self Scheduling - its free-slot
 * rebuilds tracked per location + provider on the provider mappings (latest run, trigger, status, retries, cause
 * of error). Other jobs are added later, on demand.
 */
const SyncJobs = () => {
  /** Every location + provider with its latest rebuild, null until the first load finishes. */
  const [pairs, setPairs] = useState<CalendarSyncPair[] | null>(null);
  /** True while the list is loading. */
  const [loading, setLoading] = useState<boolean>(true);
  /** Error text when the list could not be loaded. */
  const [loadError, setLoadError] = useState<string>('');
  /** When the list was last loaded. */
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  /** Location / provider name filter. */
  const [search, setSearch] = useState<string>('');
  /** Status filter; empty = all. */
  const [statusFilter, setStatusFilter] = useState<SlotRebuildStatus | ''>('');
  /**
   * MM-1198: location + provider rows the user opened (true) or closed (false); a row not in here is open (see
   * isExpanded). Kept across the auto refresh.
   */
  const [toggled, setToggled] = useState<Map<string, boolean>>(new Map());
  /** MM-1198: true while a load is running, so the auto refresh never starts a second one. */
  const loadingRef = useRef<boolean>(false);

  /** MM-1198: whether a location + provider's breaks are shown - open by default, until the user closes it. */
  const isExpanded = (pair: CalendarSyncPair) => toggled.get(pairKey(pair)) ?? true;

  /** MM-1198: shows or hides a location + provider's breaks. */
  const toggleBreaks = (pair: CalendarSyncPair) =>
    setToggled((current) => new Map(current).set(pairKey(pair), !isExpanded(pair)));

  /**
   * Loads (or reloads) the latest runs. MM-1198: also the auto refresh - a tick is skipped while a load is still
   * running.
   */
  const loadRuns = useCallback(async () => {
    if (loadingRef.current) {
      return;
    }
    loadingRef.current = true;
    try {
      const response: any = await getCalendarSyncPairsAPI();
      setPairs(response?.data ?? []);
      setLoadedAt(Date.now());
      setLoadError('');
    } catch (error: any) {
      console.error('Sync jobs load failed', error);
      // MM-1198: no Refresh button - the next auto refresh tries again
      setLoadError('Could not load the self-scheduling runs. Trying again in a few seconds.');
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  /** Loads the runs when the page opens. */
  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  /**
   * MM-1198: whether the Self Scheduling section is open - the auto refresh only runs while it is, so collapsing it
   * stops the reloads (user request, 2026-10-28).
   */
  const [sectionOpen, setSectionOpen] = useState<boolean>(true);

  /**
   * MM-1198: opens or collapses the Self Scheduling section; opening it reloads the runs at once, since nothing was
   * reloaded while it was collapsed.
   */
  const handleSectionToggle = (_event: React.SyntheticEvent, expanded: boolean) => {
    setSectionOpen(expanded);
    if (expanded) {
      loadRuns();
    }
  };

  /**
   * MM-1198: then reloads them every 10 seconds while the page is open and the Self Scheduling section is expanded
   * (only reads - nothing is triggered).
   */
  const secondsLeft = useAutoRefresh(loadRuns, sectionOpen);

  /**
   * The rows for the current filters grouped by location, as on the Sync Calendar page - locations and their
   * providers sorted by name.
   */
  const groups = useMemo(
    () =>
      groupByLocation(
        (pairs ?? []).filter((pair) => !statusFilter || statusOf(pair) === statusFilter),
        search
      ),
    [pairs, search, statusFilter]
  );

  /** How many pairs are in each status. */
  const counts = useMemo(
    () =>
      (pairs ?? []).reduce<Record<string, number>>((result, pair) => {
        result[statusOf(pair)] = (result[statusOf(pair)] ?? 0) + 1;
        return result;
      }, {}),
    [pairs]
  );

  return (
    <Container maxWidth='xl'>
      <Typography variant='h5' sx={{ paddingTop: 6 }}>
        Sync Jobs
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mt: 1, mb: 3 }}>
        Automated sync jobs in one place: what each one does, when it runs, its automatic retry, and the latest run with
        its status and cause of error.
      </Typography>

      {/* MM-1169: one accordion per job group (more groups will be added later), expanded by default; the theme's
          accordion style, as on the patient Documents page */}
      <Box sx={{ mb: 6 }}>
        <Accordion expanded={sectionOpen} onChange={handleSectionToggle}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls='self-scheduling-jobs'
            id='self-scheduling-jobs'
          >
            <Typography fontWeight={600}>Self Scheduling</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              Builds the free slots patients can book for the next 90 days from each provider&apos;s DrChrono calendar
              and appointment templates, per location + provider.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
              <TextField
                size='small'
                placeholder='Search location or provider'
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <GridSearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 280, backgroundColor: COLORS.White }}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {STATUS_FILTERS.map((item) => (
                  <Chip
                    key={item.label}
                    size='small'
                    variant={statusFilter === item.status ? 'filled' : 'outlined'}
                    color={item.color}
                    label={`${item.label}: ${item.status ? counts[item.status] ?? 0 : pairs?.length ?? 0}`}
                    onClick={() => setStatusFilter(item.status)}
                  />
                ))}
              </Box>
              <Box sx={{ ml: 'auto' }}>
                {/* MM-1198: circular countdown to the next auto refresh */}
                <AutoRefreshCountdown loadedAt={loadedAt} secondsLeft={secondsLeft} />
              </Box>
            </Box>

            {loadError ? (
              <Alert severity='error' sx={{ mb: 2 }}>
                {loadError}
              </Alert>
            ) : null}

            {loading && pairs === null ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant='outlined'>
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      {RUN_COLUMNS.map((column) => (
                        <TableCell key={column} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {column}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groups.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={RUN_COLUMNS.length} align='center' sx={{ py: 4 }}>
                          {search || statusFilter
                            ? 'No location or provider matches your filters.'
                            : 'No providers are set up for self-scheduling.'}
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {groups.map((group) => (
                      <Fragment key={group.key}>
                        <TableRow sx={{ backgroundColor: '#F4F1FF' }}>
                          <TableCell colSpan={RUN_COLUMNS.length} sx={{ fontWeight: 600 }}>
                            {group.label} ({group.pairs.length})
                          </TableCell>
                        </TableRow>
                        {group.pairs.map((pair) => {
                          // MM-1198: the run going on now, or the latest finished one when nothing waits
                          const run = currentRun(pair);
                          return (
                            <Fragment key={pairKey(pair)}>
                              {/* MM-1198: the row is an accordion - click it to show / hide the provider's breaks; only
                                when the provider has stored breaks */}
                              <TableRow
                                hover
                                onClick={hasBreaks(pair) ? () => toggleBreaks(pair) : undefined}
                                aria-expanded={hasBreaks(pair) ? isExpanded(pair) : undefined}
                                sx={{ cursor: hasBreaks(pair) ? 'pointer' : 'default' }}
                              >
                                <TableCell sx={{ pl: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {hasBreaks(pair) ? (
                                      <ExpandMoreIcon
                                        fontSize='small'
                                        sx={{
                                          color: 'text.secondary',
                                          transform: isExpanded(pair) ? 'rotate(180deg)' : 'rotate(-90deg)',
                                          transition: 'transform 0.2s',
                                        }}
                                      />
                                    ) : (
                                      // keeps the names aligned with rows that have the arrow
                                      <Box component='span' sx={{ width: 20, flexShrink: 0 }} />
                                    )}
                                    {pair.providerName}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  {run.trigger ? TRIGGER_LABELS[run.trigger] ?? run.trigger : '-'}
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(run.requestedDate)}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(run.startedDate)}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(run.completedDate)}</TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  {/* MM-1198: the status chip with the retries used under it */}
                                  <StatusChip status={run.status} />
                                  {run.retryText ? (
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                      sx={{ display: 'block', mt: 0.5 }}
                                    >
                                      {run.retryText}
                                    </Typography>
                                  ) : null}
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(run.nextRetryDate)}</TableCell>
                                <TableCell>
                                  {/* MM-1198: only a red message icon when there is an error; click for the details
                                    (the click doesn't toggle the row) */}
                                  <Box onClick={(event) => event.stopPropagation()}>
                                    <MessageCell message={run.message} title='Cause of error' error iconOnly />
                                  </Box>
                                </TableCell>
                              </TableRow>
                              {hasBreaks(pair) && isExpanded(pair) ? (
                                <TableRow>
                                  <TableCell colSpan={RUN_COLUMNS.length} sx={{ p: 0 }}>
                                    <BreakEventsTable pair={pair} />
                                  </TableCell>
                                </TableRow>
                              ) : null}
                            </Fragment>
                          );
                        })}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Container>
  );
};

export default SyncJobs;
